import React, {FC, useEffect, useState} from 'react';
import {datadogRum} from '@datadog/browser-rum';

import {Button, Input, LegacyModal, renderUtils} from '@zoefin/design-system';
import {InputProps} from '@zoefin/design-system/dist/types/components/atoms/Input/types.d';

import {OnboardingContextValue} from '../../../../stores/onboardingWizard';
import db from '../../../../utils/db';
import getFormVersion from '../../../../utils/getFormVersion';
import {addToDataLayer} from '../../../../utils/pageUtils';
import OTPAccess from '../../../commons/OTPAccess';
import {SubmitStruct} from '../../index.d';

import styles, {COMP_NAME, globalStyles} from './styles';

export interface RegistrationProps {
  structure: SubmitStruct;
  user?: Prospect;
  isVisible: boolean;
  experiments: Experiments;
  toNext: () => void;
}

const RegistrationView: FC<RegistrationProps> = ({
  structure,
  user,
  isVisible,
  toNext,
}) => {
  const [{state, updateValue, updateIsValid}] = OnboardingContextValue();

  const [openOTP, setOpenOTP] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const prevIsVisible = renderUtils.usePrevious(isVisible);

  useEffect(() => {
    if (isVisible && !prevIsVisible) {
      datadogRum.addAction('FAAPO_Form_Register', {
        Version: getFormVersion([], {}),
      });

      addToDataLayer({
        overrideBase: 'Portal_Form_Register',
        values: {
          // This form isn't wrapped with `PortalProvider`, history is empty []
          Version: getFormVersion([], {}),
        },
      });
    }
  });

  useEffect(() => {
    const emailInput = structure.inputs.filter(
      (input) => input.name === 'email',
    )[0];
    const emailValue = state[emailInput?.id]?.value as string;

    setEmail(emailValue);
  }, [structure, state]);

  const isValid = () =>
    structure.inputs.reduce((valid, input) => {
      if (!valid) {
        return valid;
      }

      if (user && (input.name === 'name' || input.name === 'email')) {
        return valid;
      }

      return state[input.id]?.isValid;
    }, true);

  const handleSubmit = async () => {
    setIsLoading(true);

    const userAvailable = await db.user.search(email);

    if (userAvailable?.id) {
      setIsLoading(false);
      setOpenOTP(true);
    } else {
      toNext();
    }
  };

  const title = user
    ? `${
        user.name.split(' ')[0]
      }, we have found the best advisor matches for you!`
    : structure.title;
  const buttonIcon = isValid()
    ? '/static/svg/arrow-line-right-white.svg'
    : '/static/svg/arrow-line-right.svg';

  if (!isVisible) {
    return null;
  }

  return (
    <div className={COMP_NAME}>
      <div className={`${COMP_NAME}__left`}>
        <img
          className={`${COMP_NAME}__left_confeti`}
          src="/static/img/onboarding/confeti_bg_2.png"
          alt="Confeti background"
        />

        <img
          className={`${COMP_NAME}__left_avatar1`}
          src="/static/img/assets/person-1.png"
          alt="Advisor avatar 1"
        />
        <img
          className={`${COMP_NAME}__left_avatar2`}
          src="/static/img/assets/person-2.png"
          alt="Advisor avatar 2"
        />
        <img
          className={`${COMP_NAME}__left_avatar3`}
          src="/static/img/assets/person-3.png"
          alt="Advisor avatar 3"
        />
        <div className={`${COMP_NAME}__left_title`}>{title}</div>
        {structure.description && (
          <div className={`${COMP_NAME}__left_subtitle`}>
            {structure.description}
          </div>
        )}

        <img
          className={`${COMP_NAME}__left_sneakpeak`}
          src="/static/img/onboarding/match_sneak_peak.png"
          alt="Match sneak peak"
        />
      </div>
      <div className={`${COMP_NAME}__right`}>
        <div className={`${COMP_NAME}__right_title`}>{title}</div>

        <div className={`${COMP_NAME}__right_fields`}>
          {structure.inputs.map((input) => {
            if (user && (input.name === 'name' || input.name === 'email')) {
              return null;
            }

            const inputProps: InputProps = {
              id: `submit_${input.name}`,
              type: input.type,
              name: input.name,
              value: state[input.id]?.value as string,
              placeholder: input.placeholder,
              label: input.title,
              fitParent: true,
              textAlign: 'center',
              required: input.validationRules?.includes('required'),
              hasError:
                state[input.id]?.value !== undefined &&
                !state[input.id].value.length,
              onChange: (value) => {
                updateValue(input.id, value);
              },
              onValidationChange: (valid) => {
                updateIsValid(input.id, valid);
              },
            };

            return (
              <div key={input.name} className={`${COMP_NAME}__input`}>
                <Input {...inputProps} />
              </div>
            );
          })}
        </div>
        <div className={`${COMP_NAME}__right_disclosures`}>
          <p>
            By sharing your e-mail address above and clicking the button below,
            you consent to the electronic delivery of Zoe Financial’s Form&nbsp;
            <a
              href="https://zoefin.com/wp-content/uploads/2025/03/ZOE-ADV-January_31_2025.pdf"
              target="_blank"
              rel="noreferrer">
              ADV 2A
            </a>
            , Form&nbsp;
            <a
              href="https://zoefin.com/wp-content/uploads/2025/03/Form-ADV-Part-2B-Delivery-February_6_2025.pdf"
              target="_blank"
              rel="noreferrer">
              CRS
            </a>
            , and&nbsp;
            <a
              href="https://zoefin.com/solicitation-disclosure/"
              target="_blank"
              rel="noreferrer">
              Solicitor’s Disclosure
            </a>
            .
          </p>
        </div>

        <div className={`${COMP_NAME}__right_button`}>
          <Button
            disabled={!isValid()}
            loading={isLoading}
            style={{width: '100%'}}
            withIcon={{
              iconSrc: buttonIcon,
            }}
            onClick={handleSubmit}
            config={{
              color: 'green',
              type: 'primary',
            }}
            as="button">
            See advisor matches
          </Button>
        </div>
      </div>
      <LegacyModal isOpen={openOTP} size="small">
        <button
          type="button"
          className={`${COMP_NAME}__otp-close-btn`}
          onClick={() => setOpenOTP(false)}>
          <img src="/static/svg/close-dark-grey.svg" alt="close modal" />
        </button>
        <OTPAccess
          initialEmail={email}
          title="You’re already registered!"
          onLogIn={() => toNext()}
          className={`${COMP_NAME}__otp-access`}
        />
      </LegacyModal>
      <style jsx>{styles}</style>
      <style jsx>{globalStyles}</style>
    </div>
  );
};

RegistrationView.defaultProps = {
  user: undefined,
};

export default RegistrationView;
